import { AxiosError } from 'axios';
import { apiService } from '@shared/services/apiServices';
import type { ContactRequestStatus } from '../types/contactRequestTypes';
import contactRequestEndpoints from './contactRequestEndpoints';
import {
  handleContactRequestError,
  ContactRequestErrorCode,
  contactRequestErrorMessages,
} from '../errors';

export interface UpdateContactRequestStatusParams {
  contactRequestId: string;
  newStatus: ContactRequestStatus;
  note?: string;
}

export interface UpdateContactRequestStatusResult {
  success: boolean;
  error?: string;
}

/**
 * PATCH contact-request/contact-requests/{id}/status
 *
 * Returns 204 on success (no body).
 * Access: SUPER_ADMIN\Admin (any) |
 */
export const updateContactRequestStatus = async ({
  contactRequestId,
  newStatus,
  note,
}: UpdateContactRequestStatusParams): Promise<UpdateContactRequestStatusResult> => {
  const trimmedId = contactRequestId.trim();
  if (!trimmedId) {
    return {
      success: false,
      error: contactRequestErrorMessages[ContactRequestErrorCode.UNKNOWN],
    };
  }

  const body: Record<string, unknown> = { new_status: newStatus };
  const trimmedNote = note?.trim() ?? '';
  if (trimmedNote.length > 0) {
    body['note'] = trimmedNote;
  }

  try {
    const result = await apiService.patchAPI(
      `${contactRequestEndpoints.LIST_CONTACT_REQUESTS}/${trimmedId}/status`,
      body,
      { skipGlobalErrorRedirect: true }
    );

    if (!result.error) {
      return { success: true };
    }

    const { errorMessage } = handleContactRequestError(
      result.status as number | undefined,
      result.data,
      !!result.isNetworkError
    );
    return { success: false, error: errorMessage };
  } catch (error) {
    if (error instanceof AxiosError) {
      const { errorMessage } = handleContactRequestError(
        error.response?.status,
        error.response?.data,
        !error.response && !!error.request
      );
      return { success: false, error: errorMessage };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : contactRequestErrorMessages[
              ContactRequestErrorCode.UNEXPECTED_ERROR
            ],
    };
  }
};
